import { useState, useEffect, useCallback } from "react";
import { ArrowClockwise, CheckCircle, XCircle, Lightning } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Manual Refresh button + modal.
 * Props:
 *  - prefillRouteIds: optional array of route IDs to pre-select
 *  - label: button label override
 *  - onCompleted: callback when job reaches terminal state
 */
export default function ManualRefreshButton({ prefillRouteIds, label, onCompleted, programme = "CI" }) {
  const { api, user } = useAuth();
  const canTrigger = user?.role === "admin" || user?.role === "analyst";
  const [open, setOpen] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [selected, setSelected] = useState(new Set(prefillRouteIds || []));
  const [estimate, setEstimate] = useState(null);
  const [quota, setQuota] = useState(null);
  const [job, setJob] = useState(null);
  const [phase, setPhase] = useState("select"); // select | running | done

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [r, q] = await Promise.all([
          api.get("/routes", { params: { programme } }),
          api.get("/serpapi/quota", { params: { programme } }),
        ]);
        setRoutes(r.data || []);
        setQuota(q.data);
        if (prefillRouteIds?.length) setSelected(new Set(prefillRouteIds));
      } catch { /* ignore */ }
    })();
  }, [open, api, prefillRouteIds, programme]);

  // Re-estimate whenever selection changes
  useEffect(() => {
    if (!open || selected.size === 0) { setEstimate({ total_searches: 0, breakdown: [] }); return; }
    (async () => {
      try {
        const { data } = await api.post("/fares/manual-refresh/estimate", { route_ids: Array.from(selected) });
        setEstimate(data);
      } catch { setEstimate(null); }
    })();
  }, [open, selected, api]);

  const toggleAll = () => {
    if (selected.size === routes.length) setSelected(new Set());
    else setSelected(new Set(routes.map(r => r.id)));
  };
  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const pollJob = useCallback(async (jobId) => {
    try {
      const { data } = await api.get(`/fares/manual-refresh/${jobId}`);
      setJob(data);
      if (data.status === "running") {
        setTimeout(() => pollJob(jobId), 3000);
      } else {
        setPhase("done");
        onCompleted?.(data);
      }
    } catch { /* retry */ setTimeout(() => pollJob(jobId), 4000); }
  }, [api, onCompleted]);

  const confirmRefresh = async () => {
    if (selected.size === 0) return;
    setPhase("running");
    try {
      const { data } = await api.post("/fares/manual-refresh", { route_ids: Array.from(selected) });
      pollJob(data.job_id);
    } catch {
      setPhase("done");
      setJob({ status: "failed", error: "Request failed" });
    }
  };

  const close = () => {
    setOpen(false);
    setPhase("select");
    setJob(null);
  };

  return (
    <>
      <div className="relative inline-block">
        <button
          onClick={() => canTrigger && setOpen(true)}
          disabled={!canTrigger}
          data-testid="manual-refresh-btn"
          title={canTrigger ? "Refresh SerpAPI fares" : "Requires Analyst or Admin role"}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
            canTrigger
              ? "bg-ci-blue text-white hover:bg-blue-600 shadow-lg hover:shadow-xl"
              : "bg-gray-700 text-gray-400 cursor-not-allowed opacity-60"
          }`}
        >
          <ArrowClockwise size={16} weight="bold" />
          {label || "Refresh Fares"}
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
          <div className="bg-[#0A1428] border border-[rgba(255,255,255,0.12)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            {phase === "select" && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-syne font-bold text-white text-xl">Manual SerpAPI Refresh</h2>
                  <button onClick={close} className="text-gray-400 hover:text-white text-xl" data-testid="manual-refresh-close">✕</button>
                </div>

                {quota && (
                  <div className={`mb-4 p-3 rounded-lg text-xs ${quota.low_balance ? "bg-red-900/40 text-red-300 border border-red-800" : "bg-[rgba(27,82,196,0.1)] text-ci-blue border border-[rgba(27,82,196,0.3)]"}`} data-testid="manual-refresh-quota">
                    <strong>{programme === "PX" ? "Air Niugini" : "China Airlines"} SerpAPI · {quota.plan_name}:</strong> {quota.total_searches_left?.toLocaleString()} searches remaining of {quota.searches_per_month?.toLocaleString()}/month
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center p-2 bg-[rgba(255,255,255,0.03)] rounded-lg border border-[rgba(255,255,255,0.08)]">
                    <label className="flex items-center gap-3 cursor-pointer w-full">
                      <input
                        type="checkbox"
                        checked={selected.size === routes.length && routes.length > 0}
                        onChange={toggleAll}
                        data-testid="manual-refresh-all"
                        className="w-4 h-4 accent-ci-blue"
                      />
                      <span className="text-white font-semibold text-sm">All Routes ({routes.length})</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {routes.map(r => (
                      <label key={r.id} className="flex items-center gap-2 p-2 bg-[rgba(255,255,255,0.02)] rounded-lg hover:bg-[rgba(255,255,255,0.05)] cursor-pointer border border-[rgba(255,255,255,0.06)]">
                        <input
                          type="checkbox"
                          checked={selected.has(r.id)}
                          onChange={() => toggleOne(r.id)}
                          data-testid={`manual-refresh-route-${r.destination_code}`}
                          className="w-4 h-4 accent-ci-blue"
                        />
                        <span className="text-white text-sm font-mono">{r.origin_code}-{r.destination_code}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-[rgba(27,82,196,0.08)] rounded-xl border border-[rgba(27,82,196,0.3)]">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-text-secondary">Estimated SerpAPI cost</p>
                    <p className="font-mono font-bold text-2xl text-ci-blue" data-testid="manual-refresh-estimate">
                      {estimate?.total_searches ?? 0} searches
                    </p>
                    <p className="text-[10px] text-text-secondary mt-1">{programme === "PX" ? "18 per route" : "27 per route"} × {selected.size} route{selected.size !== 1 ? "s" : ""} ({programme === "PX" ? "9 months × 2 cabins" : "9 months × 3 cabins"})</p>
                  </div>
                  <button
                    onClick={confirmRefresh}
                    disabled={selected.size === 0 || (programme !== "PX" && quota && estimate?.total_searches > quota.total_searches_left)}
                    data-testid="manual-refresh-confirm"
                    className="px-5 py-3 rounded-xl bg-ci-blue hover:bg-blue-600 text-white font-bold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
                  >
                    <Lightning size={18} weight="fill" />
                    Confirm & Fetch
                  </button>
                </div>
                {quota && estimate?.total_searches > quota.total_searches_left && (
                  <p className="mt-2 text-xs text-red-400 text-center" data-testid="manual-refresh-quota-warning">
                    ⚠ Estimated {estimate.total_searches} searches exceeds remaining balance of {quota.total_searches_left}
                    {programme === "PX" && " — SerpAPI will be skipped; Trip.com (Zyte/Bright Data), Duffel and Kiwi tiers still run"}
                  </p>
                )}
              </>
            )}

            {phase === "running" && (
              <div className="py-8 text-center space-y-4" data-testid="manual-refresh-running">
                <ArrowClockwise size={48} weight="bold" className="text-ci-blue animate-spin mx-auto" />
                <h3 className="font-syne font-bold text-white text-xl">Refreshing fares…</h3>
                <p className="text-text-secondary text-sm">
                  {job?.routes_completed?.length || 0} of {selected.size} routes complete
                </p>
                {job?.routes_completed?.length > 0 && (
                  <p className="text-xs text-emerald-400 font-mono">✓ {job.routes_completed.join(", ")}</p>
                )}
                {job?.routes_skipped_quota?.length > 0 && (
                  <p className="text-xs text-amber-400 font-mono">⏸ Quota-skipped: {job.routes_skipped_quota.join(", ")}</p>
                )}
                <p className="text-[10px] text-text-secondary">Running in background — safe to close this modal</p>
              </div>
            )}

            {phase === "done" && job && (
              <div className="py-6 space-y-4" data-testid="manual-refresh-done">
                <div className="text-center">
                  {job.status === "success" && <><CheckCircle size={48} weight="fill" className="text-emerald-400 mx-auto" /><h3 className="font-syne font-bold text-white text-xl mt-3">Refresh complete</h3></>}
                  {job.status === "partial" && <><CheckCircle size={48} weight="fill" className="text-amber-400 mx-auto" /><h3 className="font-syne font-bold text-white text-xl mt-3">Refresh partial</h3></>}
                  {job.status === "quota_exhausted" && <><XCircle size={48} weight="fill" className="text-amber-400 mx-auto" /><h3 className="font-syne font-bold text-white text-xl mt-3">Quota exhausted mid-run</h3></>}
                  {job.status === "failed" && <><XCircle size={48} weight="fill" className="text-red-400 mx-auto" /><h3 className="font-syne font-bold text-white text-xl mt-3">Refresh failed</h3></>}
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-[rgba(255,255,255,0.04)] p-3 rounded-lg">
                    <p className="text-[10px] uppercase text-text-secondary">Cells filled</p>
                    <p className="font-mono font-bold text-2xl text-emerald-400" data-testid="manual-refresh-cells-filled">+{job.cells_filled ?? 0}</p>
                  </div>
                  <div className="bg-[rgba(255,255,255,0.04)] p-3 rounded-lg">
                    <p className="text-[10px] uppercase text-text-secondary">Successful calls</p>
                    <p className="font-mono font-bold text-2xl text-ci-blue">{job.successful_calls ?? 0}</p>
                  </div>
                  <div className="bg-[rgba(255,255,255,0.04)] p-3 rounded-lg">
                    <p className="text-[10px] uppercase text-text-secondary">Duration</p>
                    <p className="font-mono font-bold text-2xl text-white">{job.duration_seconds ?? 0}s</p>
                  </div>
                </div>
                {job.routes_skipped_quota?.length > 0 && (
                  <p className="text-xs text-amber-300 text-center">Skipped due to quota: {job.routes_skipped_quota.join(", ")}</p>
                )}
                <div className="text-center">
                  <button onClick={close} className="px-6 py-2 rounded-lg bg-ci-blue text-white font-semibold hover:bg-blue-600" data-testid="manual-refresh-close-done">Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
